import React, { useEffect, useState, useRef } from 'react';
import { useGameStore } from '../store/gameStore';
import { getHandValue } from '../utils/deck';

/**
 * Screen reader announcer component
 * Uses ARIA live regions to announce game events to screen reader users
 */
export const ScreenReaderAnnouncer: React.FC = () => {
  const { 
    playerHands, 
    currentHandIndex, 
    dealerHand, 
    roundComplete, 
    isPlaying,
    bankroll,
    insuranceOffered
  } = useGameStore();
  
  const [announcement, setAnnouncement] = useState('');
  const prevStateRef = useRef({
    roundComplete: false,
    isPlaying: false,
    playerCardsCount: 0,
    insuranceOffered: false
  });

  useEffect(() => {
    const prev = prevStateRef.current;
    const currentHand = playerHands[currentHandIndex];
    
    // New hand dealt
    if (!prev.isPlaying && isPlaying) {
      const { value: playerValue } = currentHand ? getHandValue(currentHand.cards) : { value: 0 };
      const dealerCard = dealerHand.cards[0];
      setAnnouncement(
        `New hand dealt. Your hand: ${playerValue}. Dealer shows: ${dealerCard?.rank || 'unknown'}.`
      );
    }
    
    // Insurance offered
    if (!prev.insuranceOffered && insuranceOffered) {
      setAnnouncement('Dealer shows an Ace. Insurance is available.');
    }
    
    // Hand complete
    if (!prev.roundComplete && roundComplete) {
      const results = playerHands.map((hand, i) => {
        const { value } = getHandValue(hand.cards);
        return `Hand ${i + 1}: ${value}, ${hand.result || 'unknown'}`;
      }).join('. ');
      
      const dealerValue = getHandValue(dealerHand.cards).value;
      setAnnouncement(
        `Round complete. ${results}. Dealer has ${dealerValue}. Bankroll: $${bankroll}.`
      );
    }
    
    // Player card count changed (new card dealt to player)
    const currentCardsCount = currentHand?.cards?.length || 0;
    if (isPlaying && !roundComplete && currentCardsCount > prev.playerCardsCount) {
      const newCard = currentHand?.cards?.[currentCardsCount - 1];
      const { value } = currentHand ? getHandValue(currentHand.cards) : { value: 0 };
      if (newCard && prev.playerCardsCount > 0) {
        setAnnouncement(
          `Dealt ${newCard.rank} of ${newCard.suit}. Hand total: ${value}.`
        );
      }
    }
    
    // Update previous state
    prevStateRef.current = {
      roundComplete,
      isPlaying,
      playerCardsCount: currentCardsCount,
      insuranceOffered
    };
  }, [playerHands, currentHandIndex, dealerHand, roundComplete, isPlaying, bankroll, insuranceOffered]);

  // Clear announcement after it's read
  useEffect(() => {
    if (announcement) {
      const timer = setTimeout(() => setAnnouncement(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [announcement]);

  return (
    <>
      {/* Polite announcements for game updates */}
      <div 
        role="status" 
        aria-live="polite" 
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>
      
      {/* Assertive announcements for critical updates */}
      <div 
        role="alert" 
        aria-live="assertive" 
        aria-atomic="true"
        className="sr-only"
      >
        {insuranceOffered && 'Insurance available - press I to accept or N to decline'}
      </div>
    </>
  );
};




